import React from 'react';
import { CheckIcon, InboxIcon, XIcon } from 'lucide-react';
import type { Navigate, PtoRequest } from '../../types/hr';
import { displayName, getEmployee } from '../../utils/employees';
import { formatDays, formatRange, relativeDay } from '../../utils/format';
import { policyName } from '../../utils/timeOff';
import { Avatar } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { EmptyState } from '../ui/EmptyState';
interface RequestsQueueProps {
  requests: PtoRequest[];
  onDecide: (id: string, status: 'approved' | 'denied') => void;
  onNavigate: Navigate;
}
export function RequestsQueue({
  requests,
  onDecide,
  onNavigate
}: RequestsQueueProps) {
  const pending = requests.filter(r => r.status === 'pending');
  const decided = requests.filter(r => r.status !== 'pending');
  return <div className="space-y-6" data-id="element-659" data-mp="32jckly1wbkq7">
      <Card title="Pending requests" subtitle={`${pending.length} awaiting decision`} icon={InboxIcon} bodyClassName="pb-2" data-id="element-660" data-mp="32j2tu89119jg">
        {pending.length === 0 ? <div className="px-6 pb-4" data-id="element-661" data-mp="ok5hxi0yvrjw">
            <EmptyState icon={InboxIcon} title="All caught up" description="There are no time off requests waiting for approval." data-id="element-662" data-mp="oj3zutlja4so" />
          </div> : <RequestTable requests={pending} onDecide={onDecide} onNavigate={onNavigate} data-id="element-663" data-mp="eu3w6ww36l85" />}
      </Card>

      {decided.length > 0 && <Card title="Recently decided" subtitle="This session" icon={CheckIcon} bodyClassName="pb-2" data-id="element-664" data-mp="t9n0c17sg8f">
          <RequestTable requests={decided} onNavigate={onNavigate} data-id="element-665" data-mp="5vie12uj187w" />
        </Card>}
    </div>;
}
interface RequestTableProps {
  requests: PtoRequest[];
  onDecide?: (id: string, status: 'approved' | 'denied') => void;
  onNavigate: Navigate;
}
function RequestTable({
  requests,
  onDecide,
  onNavigate
}: RequestTableProps) {
  return <div className="overflow-x-auto" data-id="element-666" data-mp="1q9ir7v4qj30m">
      <table className="w-full min-w-[980px] text-left text-sm" data-id="element-667" data-mp="1q8hihxb6ysm2">
        <thead data-id="element-668" data-mp="1ot4z72kihvwm">
          <tr className="border-y border-slate-100 bg-canvas/70 text-[11px] font-semibold uppercase tracking-wider text-muted" data-id="element-669" data-mp="1otn3mmhbvgfm">
            <th scope="col" className="px-6 py-2.5" data-id="element-670" data-mp="1js7hss7bt5w6">Employee</th>
            <th scope="col" className="px-6 py-2.5" data-id="element-671" data-mp="1etnxew8ds25x">Dates</th>
            <th scope="col" className="px-6 py-2.5" data-id="element-672" data-mp="1asu6rv3k099c">Policy</th>
            <th scope="col" className="px-6 py-2.5" data-id="element-673" data-mp="1auf39q9fi29u">Note</th>
            <th scope="col" className="px-6 py-2.5 text-right" data-id="element-674" data-mp="27jmo0w2847cp">Balance after</th>
            <th scope="col" className="px-6 py-2.5" data-id="element-675" data-mp="22k0eoxpinb96">Submitted</th>
            <th scope="col" className="px-6 py-2.5 text-right" data-id="element-676" data-mp="22ks6dxfzsou6">{onDecide ? 'Decision' : 'Status'}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100" data-id="element-677" data-mp="1trolx56xjbq1">
          {requests.map(r => {
          const emp = getEmployee(r.employeeId);
          if (!emp) return null;
          const after = r.balanceBefore - r.days;
          return <tr key={r.id} className="transition-colors hover:bg-azure/[0.03]" data-id="element-678" data-mp="2li2x2tnk69y0">
                <td className="px-6 py-3.5" data-id="element-679" data-mp="819w1pkp3u7h">
                  <button type="button" onClick={() => onNavigate({
                page: 'profile',
                employeeId: emp.id,
                tab: 'timeoff'
              })} className="flex items-center gap-3 text-left" data-id="element-680" data-mp="82ky9kyhstwh">
                    <Avatar name={displayName(emp)} seed={emp.id} size="sm" data-id="element-681" data-mp="gzd5hniep0ix" />
                    <span data-id="element-682" data-mp="m12gx49jjg7j">
                      <span className="block font-medium text-navy hover:text-azure" data-id="element-683" data-mp="m1avr1p84vld">{displayName(emp)}</span>
                      <span className="block text-xs text-muted" data-id="element-684" data-mp="qwzz7g4d6wmy">{emp.department}</span>
                    </span>
                  </button>
                </td>
                <td className="whitespace-nowrap px-6 py-3.5" data-id="element-685" data-mp="10pymmuo4kh4z">
                  <span className="block font-medium text-slate-800" data-id="element-686" data-mp="14qheuptxjp7p">{formatRange(r.start, r.end)}</span>
                  <span className="block text-xs text-muted" data-id="element-687" data-mp="19pu57kk0fef1">{formatDays(r.days)}</span>
                </td>
                <td className="px-6 py-3.5" data-id="element-688" data-mp="1eo5cbnix7atk">
                  <Badge tone={r.policyId === 'newhire' ? 'navy' : 'blue'} data-id="element-689" data-mp="1eoybo3fjuy1o">{policyName(r.policyId)}</Badge>
                </td>
                <td className="max-w-[240px] px-6 py-3.5 text-xs italic text-slate-500" data-id="element-690" data-mp="10zlk2ug59p2m">“{r.note}”</td>
                <td className="whitespace-nowrap px-6 py-3.5 text-right tabular-nums" data-id="element-691" data-mp="1arrj2o4td6rb">
                  <span className={`font-semibold ${after < 0 ? 'text-rose-600' : 'text-navy'}`} data-id="element-692" data-mp="1atlu9aqa9ki7">{after} days</span>
                  <span className="block text-[11px] text-muted" data-id="element-693" data-mp="i33q06dtpd37">from {r.balanceBefore}</span>
                </td>
                <td className="whitespace-nowrap px-6 py-3.5 text-slate-600" data-id="element-694" data-mp="d4ircbe7z1gv">{relativeDay(r.submitted)}</td>
                <td className="px-6 py-3.5 text-right" data-id="element-695" data-mp="s1jkmjc274hg">
                  {onDecide ? <div className="flex justify-end gap-2" data-id="element-696" data-mp="n1nf2fnpr4zs">
                      <Button size="sm" variant="secondary" icon={XIcon} onClick={() => onDecide(r.id, 'denied')} data-id="element-697" data-mp="n49okku5zukn">
                        Deny
                      </Button>
                      <Button size="sm" variant="primary" icon={CheckIcon} onClick={() => onDecide(r.id, 'approved')} data-id="element-698" data-mp="1ygtydpbz6nin">
                        Approve
                      </Button>
                    </div> : <Badge tone={r.status === 'approved' ? 'green' : 'red'} dot data-id="element-699" data-mp="2axl9op030167">
                      {r.status === 'approved' ? 'Approved' : 'Denied'}
                    </Badge>}
                </td>
              </tr>;
        })}
        </tbody>
      </table>
    </div>;
}